import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/lib/content";
import { sectionMastery } from "@/lib/progress";
import { ProgressBar } from "@/components/Bits";
import { ArrowRight, Crown } from "lucide-react";
import { useSub } from "@/lib/subscription";
import { isFreeSection } from "@/lib/gating";
import { LockBadge } from "@/components/UpgradeGate";

const tileBg = { yellow: "bg-amber-100 border-amber-300", blue: "bg-sky-100 border-sky-300", green: "bg-emerald-100 border-emerald-300", pink: "bg-pink-100 border-pink-300", purple: "bg-violet-100 border-violet-300", orange: "bg-orange-100 border-orange-300", sky: "bg-cyan-100 border-cyan-300" };

export default function Learn() {
  const { premium } = useSub();
  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-8">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="text-center pt-4">
          <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Learning Library</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-purple-900 mt-1">What shall we learn today?</h1>
          <p className="text-purple-700/70 font-semibold mt-2">{CATEGORIES.length} sections · {CATEGORIES.reduce((a, c) => a + c.modes.length, 0)} activities</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {CATEGORIES.map(c => {
            const pct = sectionMastery(c.id);
            const locked = !premium && !isFreeSection(c.id);
            return (
              <div key={c.id} className={`relative ll-tile ${tileBg[c.color]}`} data-testid={`learn-card-${c.id}`}>
                {locked && <LockBadge className="absolute top-3 right-3" />}
                {!premium && isFreeSection(c.id) && <span className="absolute top-3 right-3 bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow" data-testid={`free-badge-${c.id}`}>Free</span>}
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-2xl bg-white/80 flex items-center justify-center overflow-hidden shrink-0">
                    <img src={c.image} alt={c.title} className="w-full h-full object-contain p-2 ll-float-slow" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: c.emoji, className: "text-6xl" })); }} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-2xl text-purple-900">{c.title}</div>
                    <div className="text-purple-700/80 text-sm font-semibold">{c.desc}</div>
                    <div className="mt-2"><ProgressBar percent={pct} /></div>
                    <div className="text-xs mt-1 text-purple-500 font-bold">{pct}% explored · {c.modes.length} activities</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {c.modes.map(m => <span key={m.id} className="text-[11px] font-bold bg-white/70 text-purple-700 px-2 py-0.5 rounded-full">{m.emoji} {m.title}</span>)}
                </div>
                {locked ? (
                  <Link to="/pricing" data-testid={`learn-unlock-${c.id}`} className="ll-btn mt-4 w-full bg-yellow-300 text-purple-900 shadow-[0_6px_0_0_#CA8A04]"><Crown size={18} /> Unlock with Premium</Link>
                ) : (
                  <Link to={c.path} data-testid={`learn-start-${c.id}`} className="ll-btn ll-btn-primary mt-4 w-full">{pct > 0 ? "Continue" : "Start"} <ArrowRight size={18} /></Link>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
